import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plugs, 
  Clock, 
  Lightning, 
  Warning, 
  ArrowUpRight,
  CaretRight,
  CheckCircle,
  XCircle,
  Gauge,
  Database,
  Heartbeat,
  ArrowDown,
  ArrowUp,
  Circle
} from '@phosphor-icons/react';
import { API } from '@/App';
import OnboardingWizard from '@/components/OnboardingWizard';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [intel, setIntel] = useState(null);
  const [quotas, setQuotas] = useState(null);
  const [retentionHealth, setRetentionHealth] = useState(null);
  const [apiHealth, setApiHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, logsRes, intelRes, quotaRes, retRes, healthRes] = await Promise.all([
          fetch(`${API}/stats`, { credentials: 'include' }),
          fetch(`${API}/logs?limit=5`, { credentials: 'include' }),
          fetch(`${API}/analytics/intelligence`, { credentials: 'include' }),
          fetch(`${API}/settings/quotas`, { credentials: 'include' }),
          fetch(`${API}/retention/health`, { credentials: 'include' }),
          fetch(`${API}/connections/health`, { credentials: 'include' }),
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (logsRes.ok) setRecentLogs(await logsRes.json());
        if (intelRes.ok) setIntel(await intelRes.json());
        if (quotaRes.ok) setQuotas(await quotaRes.json());
        if (retRes.ok) setRetentionHealth(await retRes.json());
        if (healthRes.ok) setApiHealth(await healthRes.json());
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    if (status < 300) return 'text-kova-green';
    if (status < 400) return 'text-kova-cyan';
    if (status < 500) return 'text-kova-amber';
    return 'text-kova-red';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="font-mono text-kova-cyan">
          <span className="ascii-spinner mr-2"></span>
          LOADING DASHBOARD
        </span>
      </div>
    );
  }

  const chartData = stats?.execution_trend?.map(item => ({
    date: item._id.slice(5),
    success: item.success,
    failed: item.failed,
    total: item.total,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Onboarding Wizard */}
      <OnboardingWizard />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          className="stat-card cursor-pointer hover:border-kova-cyan/25 transition-colors"
          onClick={() => navigate('/connections')}
          data-testid="stat-connections"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="stat-value text-white">
                {stats?.connections?.active || 0}
                <span className="text-kova-slate text-lg ml-1">/ {stats?.connections?.total || 0}</span>
              </div>
              <div className="stat-label">ACTIVE CONNECTIONS</div>
            </div>
            <Plugs size={24} className="text-kova-cyan" weight="duotone" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="stat-card cursor-pointer hover:border-kova-cyan/25 transition-colors"
          onClick={() => navigate('/scheduler')}
          data-testid="stat-jobs"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="stat-value text-white">
                {stats?.jobs?.active || 0}
                <span className="text-kova-slate text-lg ml-1">/ {stats?.jobs?.total || 0}</span>
              </div>
              <div className="stat-label">SCHEDULED JOBS</div>
            </div>
            <Clock size={24} className="text-kova-cyan" weight="duotone" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="stat-card cursor-pointer hover:border-kova-cyan/25 transition-colors"
          onClick={() => navigate('/logs')}
          data-testid="stat-executions"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="stat-value text-white">
                {stats?.executions_24h?.total || 0}
              </div>
              <div className="stat-label">EXECUTIONS (24H)</div>
            </div>
            <Lightning size={24} className="text-kova-green" weight="duotone" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="stat-card cursor-pointer hover:border-kova-cyan/25 transition-colors"
          onClick={() => navigate('/logs?status=error')}
          data-testid="stat-errors"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className={`stat-value ${stats?.executions_24h?.failed > 0 ? 'text-kova-red' : 'text-white'}`}>
                {stats?.executions_24h?.failed || 0}
              </div>
              <div className="stat-label">FAILED (24H)</div>
            </div>
            <Warning size={24} className={stats?.executions_24h?.failed > 0 ? 'text-kova-red' : 'text-kova-slate'} weight="duotone" />
          </div>
        </motion.div>
      </div>

      {/* API Health Dashboard Widget */}
      {apiHealth && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.31 }}
          className="card-brutal p-0 overflow-hidden"
          data-testid="api-health-widget"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-kova-cyan/12">
            <div className="flex items-center gap-2">
              <Heartbeat size={16} className="text-kova-cyan" weight="duotone" />
              <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">API Health Monitor</span>
            </div>
            <div className="flex items-center gap-3">
              {apiHealth.overall.down > 0 && (
                <span className="text-[9px] font-mono text-kova-red bg-kova-red/10 border border-kova-red/30 px-2 py-0.5 tracking-wider">{apiHealth.overall.down} DOWN</span>
              )}
              {apiHealth.overall.degraded > 0 && (
                <span className="text-[9px] font-mono text-kova-gold bg-kova-gold/10 border border-kova-gold/30 px-2 py-0.5 tracking-wider">{apiHealth.overall.degraded} DEGRADED</span>
              )}
              <span className="text-[9px] font-mono text-kova-cyan bg-kova-cyan/10 border border-kova-cyan/30 px-2 py-0.5 tracking-wider">{apiHealth.overall.healthy} HEALTHY</span>
              <button onClick={() => navigate('/connections')} className="text-[9px] font-mono text-kova-slate hover:text-kova-cyan transition-colors">View All</button>
            </div>
          </div>

          {/* Overall Stats Strip */}
          <div className="grid grid-cols-4 border-b border-kova-cyan/12">
            <div className="px-4 py-3 border-r border-kova-cyan/12">
              <div className="text-[22px] font-heading font-bold" style={{ color: apiHealth.overall.uptime_pct >= 99 ? '#1D9E75' : apiHealth.overall.uptime_pct >= 90 ? '#C8A030' : '#E24B4A' }}>
                {apiHealth.overall.uptime_pct}%
              </div>
              <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px]">Overall Uptime</div>
            </div>
            <div className="px-4 py-3 border-r border-kova-cyan/12">
              <div className="text-[22px] font-heading font-bold text-kova-cyan">
                {apiHealth.overall.avg_latency_ms}<span className="text-[12px] text-kova-slate ml-0.5">ms</span>
              </div>
              <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px]">Avg Latency</div>
            </div>
            <div className="px-4 py-3 border-r border-kova-cyan/12">
              <div className="text-[22px] font-heading font-bold text-white">{apiHealth.overall.total}</div>
              <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px]">Integrations</div>
            </div>
            <div className="px-4 py-3">
              <div className="text-[22px] font-heading font-bold" style={{ color: apiHealth.overall.down > 0 ? '#E24B4A' : '#1D9E75' }}>
                {apiHealth.overall.down > 0 ? 'ALERT' : 'OK'}
              </div>
              <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px]">System Status</div>
            </div>
          </div>

          {/* Per-Connection Health Table */}
          {apiHealth.connections.length > 0 ? (
            <div>
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-0 px-4 py-2 text-[9px] font-mono text-kova-slate uppercase tracking-wider border-b border-kova-cyan/12 bg-kova-card">
                <div className="col-span-1">Status</div>
                <div className="col-span-3">Integration</div>
                <div className="col-span-2">Uptime</div>
                <div className="col-span-2">Latency</div>
                <div className="col-span-2">Calls (24h)</div>
                <div className="col-span-2">Trend</div>
              </div>
              {/* Table Rows */}
              {apiHealth.connections.map((conn) => (
                <div
                  key={conn.connection_id}
                  className="grid grid-cols-12 gap-0 px-4 py-2.5 border-b border-[#1a1a1a] hover:bg-kova-bg cursor-pointer transition-colors items-center"
                  onClick={() => navigate(`/connections/${conn.connection_id}`)}
                  data-testid={`health-row-${conn.connection_id}`}
                >
                  {/* Status Indicator */}
                  <div className="col-span-1">
                    <Circle
                      size={10}
                      weight="fill"
                      className={
                        conn.status === 'healthy' ? 'text-kova-green' :
                        conn.status === 'degraded' ? 'text-kova-gold' :
                        conn.status === 'down' ? 'text-kova-red' :
                        conn.status === 'no_data' ? 'text-kova-slate' :
                        'text-kova-cyan/25'
                      }
                    />
                  </div>
                  {/* Integration Name */}
                  <div className="col-span-3">
                    <div className="text-[12px] font-medium text-white truncate">{conn.name}</div>
                    <div className="text-[9px] font-mono text-kova-slate truncate">{conn.base_url}</div>
                  </div>
                  {/* Uptime */}
                  <div className="col-span-2">
                    <span className="font-mono text-[13px]" style={{
                      color: conn.uptime_pct >= 99 ? '#1D9E75' : conn.uptime_pct >= 90 ? '#C8A030' : '#E24B4A'
                    }}>
                      {conn.uptime_pct}%
                    </span>
                  </div>
                  {/* Latency */}
                  <div className="col-span-2">
                    <span className="font-mono text-[13px] text-kova-silv">
                      {conn.avg_latency_ms > 0 ? `${conn.avg_latency_ms}ms` : '\u2014'}
                    </span>
                    {conn.avg_latency_ms > 500 && (
                      <ArrowUp size={10} className="text-kova-red inline ml-1" weight="bold" />
                    )}
                  </div>
                  {/* Calls */}
                  <div className="col-span-2">
                    <span className="font-mono text-[13px] text-kova-silv">{conn.calls_24h}</span>
                    {conn.failed_24h > 0 && (
                      <span className="text-[9px] font-mono text-kova-red ml-1.5">({conn.failed_24h} err)</span>
                    )}
                  </div>
                  {/* Latency Trend Sparkline */}
                  <div className="col-span-2 h-[28px]">
                    {conn.latency_trend.length > 1 ? (
                      <ResponsiveContainer width="100%" height={28}>
                        <AreaChart data={conn.latency_trend}>
                          <defs>
                            <linearGradient id={`spark-${conn.connection_id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#0ABFA3" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#0ABFA3" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area
                            type="monotone"
                            dataKey="avg_latency"
                            stroke="#0ABFA3"
                            strokeWidth={1.5}
                            fill={`url(#spark-${conn.connection_id})`}
                            dot={false}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <span className="text-[9px] font-mono text-kova-cyan/25">No trend data</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-8 text-center">
              <p className="text-kova-slate font-mono text-sm">No connections configured</p>
              <button
                onClick={() => navigate('/connections')}
                className="mt-3 text-kova-cyan text-sm font-mono hover:underline"
              >
                Add your first API connection &rarr;
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* API Usage Quotas Card */}
      {quotas && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32 }}
          className="card-brutal p-4"
          data-testid="quota-widget"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Gauge size={16} className="text-kova-cyan" weight="duotone" />
              <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">API Usage</span>
            </div>
            <button onClick={() => navigate('/settings')} className="text-[9px] font-mono text-kova-slate hover:text-kova-cyan transition-colors">Configure</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] font-mono text-kova-slate mb-1">Hourly</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-kova-cyan/12 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${Math.min(100, (quotas.hourly_usage / quotas.hourly_limit) * 100)}%`,
                    background: quotas.hourly_remaining < quotas.hourly_limit * 0.1 ? '#E24B4A' : quotas.hourly_remaining < quotas.hourly_limit * 0.3 ? '#C8A030' : '#0ABFA3',
                  }} />
                </div>
                <span className="text-[10px] font-mono text-kova-silv">{quotas.hourly_usage}/{quotas.hourly_limit}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono text-kova-slate mb-1">Daily</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-kova-cyan/12 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${Math.min(100, (quotas.daily_usage / quotas.daily_limit) * 100)}%`,
                    background: quotas.daily_remaining < quotas.daily_limit * 0.1 ? '#E24B4A' : quotas.daily_remaining < quotas.daily_limit * 0.3 ? '#C8A030' : '#0ABFA3',
                  }} />
                </div>
                <span className="text-[10px] font-mono text-kova-silv">{quotas.daily_usage}/{quotas.daily_limit}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Retention Storage Health Card */}
      {retentionHealth && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.36 }}
          className="card-brutal p-4"
          data-testid="retention-health-widget"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Database size={16} className="text-kova-cyan" weight="duotone" />
              <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Data Retention</span>
            </div>
            <button onClick={() => navigate('/retention')} className="text-[9px] font-mono text-kova-slate hover:text-kova-cyan transition-colors">Manage</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-lg font-heading font-bold">
                {retentionHealth.storage_bytes_used < 1024 * 1024 
                  ? (retentionHealth.storage_bytes_used / 1024).toFixed(1) + ' KB'
                  : retentionHealth.storage_bytes_used < 1024 * 1024 * 1024
                    ? (retentionHealth.storage_bytes_used / (1024 * 1024)).toFixed(1) + ' MB'
                    : (retentionHealth.storage_bytes_used / (1024 * 1024 * 1024)).toFixed(2) + ' GB'}
              </div>
              <div className="text-[9px] font-mono text-kova-slate">stored</div>
            </div>
            <div>
              <div className="text-lg font-heading font-bold">{Object.values(retentionHealth.record_counts || {}).reduce((a, b) => a + b, 0)}</div>
              <div className="text-[9px] font-mono text-kova-slate">records</div>
            </div>
            <div>
              <div className="text-lg font-heading font-bold">{retentionHealth.retention_policies?.length || 0}</div>
              <div className="text-[9px] font-mono text-kova-slate">policies</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Integration Intelligence Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="card-brutal p-5"
        data-testid="intelligence-card"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-sm tracking-wide uppercase">Integration Intelligence</h2>
          <span className="text-[10px] font-mono tracking-[2px] text-kova-cyan/60" title="MATR Intelligence Layer">&#9672; MATR</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Template Counts */}
          <div className="space-y-2">
            <div className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Connected Templates</div>
            {intel?.template_counts?.length > 0 ? (
              <div className="space-y-1.5">
                {intel.template_counts.slice(0, 4).map((t) => (
                  <div key={t.provider} className="flex items-center justify-between text-xs">
                    <span className="text-kova-silv">{t.provider}</span>
                    <span className="font-mono text-kova-cyan">{t.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-kova-slate">No templates used yet</div>
            )}
          </div>
          {/* Health Rate */}
          <div className="space-y-2">
            <div className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Connection Health</div>
            <div className="text-3xl font-heading font-bold" style={{ color: (intel?.health?.rate ?? 100) >= 80 ? '#1D9E75' : (intel?.health?.rate ?? 100) >= 50 ? '#C8A030' : '#E24B4A' }}>
              {intel?.health?.rate ?? 100}%
            </div>
            <div className="text-[10px] font-mono text-kova-slate">
              {intel?.health?.healthy ?? 0} healthy / {intel?.health?.total ?? 0} total
            </div>
          </div>
          {/* Schema Health */}
          <div className="space-y-2" data-testid="schema-health-score">
            <div className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Schema Health</div>
            <div className="text-3xl font-heading font-bold" style={{ color: (intel?.schema_health?.rate ?? 100) >= 80 ? '#1D9E75' : (intel?.schema_health?.rate ?? 100) >= 50 ? '#C8A030' : '#E24B4A' }}>
              {intel?.schema_health?.rate ?? 100}%
            </div>
            <div className="text-[10px] font-mono text-kova-slate">
              {intel?.schema_health?.clean ?? 0} clean / {intel?.schema_health?.monitored ?? 0} monitored
            </div>
          </div>
          {/* Activity Feed */}
          <div className="space-y-2">
            <div className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Recent Activity</div>
            {intel?.activity_feed?.length > 0 ? (
              <div className="space-y-1">
                {intel.activity_feed.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      item.status === 'ok' ? 'bg-kova-green' : 
                      item.status === 'critical' ? 'bg-kova-red' : 
                      item.status === 'warning' ? 'bg-kova-gold' :
                      item.status === 'error' ? 'bg-kova-red' : 'bg-kova-cyan'
                    }`} />
                    <span className="text-kova-silv truncate">{item.label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-kova-slate">No recent activity</div>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Execution Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2 card-brutal p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-lg">Execution Trend</h2>
            <span className="text-kova-slate font-mono text-xs">LAST 7 DAYS</span>
          </div>
          
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <XAxis 
                  dataKey="date" 
                  stroke="#3D5A70" 
                  tick={{ fill: '#3D5A70', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                  axisLine={{ stroke: 'rgba(10,191,163,0.12)' }}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#3D5A70" 
                  tick={{ fill: '#3D5A70', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                  axisLine={{ stroke: 'rgba(10,191,163,0.12)' }}
                  tickLine={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    background: '#0C1220', 
                    border: '1px solid rgba(10,191,163,0.12)',
                    fontFamily: 'JetBrains Mono',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="success" 
                  stroke="#1D9E75" 
                  strokeWidth={2}
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="failed" 
                  stroke="#E24B4A" 
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center border border-dashed border-kova-cyan/12">
              <p className="text-kova-slate font-mono text-sm">No execution data yet</p>
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card-brutal p-6"
        >
          <h2 className="font-heading font-bold text-lg mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <button
              data-testid="quick-add-connection"
              onClick={() => navigate('/connections?action=new')}
              className="w-full flex items-center justify-between p-3 border border-kova-cyan/12 hover:border-kova-cyan hover:bg-kova-cyan/5 transition-colors text-left"
            >
              <span className="text-sm">Add API Connection</span>
              <ArrowUpRight size={16} className="text-kova-cyan" />
            </button>
            <button
              data-testid="quick-run-api"
              onClick={() => navigate('/executor')}
              className="w-full flex items-center justify-between p-3 border border-kova-cyan/12 hover:border-kova-cyan hover:bg-kova-cyan/5 transition-colors text-left"
            >
              <span className="text-sm">Execute API Call</span>
              <ArrowUpRight size={16} className="text-kova-cyan" />
            </button>
            <button
              data-testid="quick-schedule"
              onClick={() => navigate('/scheduler?action=new')}
              className="w-full flex items-center justify-between p-3 border border-kova-cyan/12 hover:border-kova-cyan hover:bg-kova-cyan/5 transition-colors text-left"
            >
              <span className="text-sm">Create Schedule</span>
              <ArrowUpRight size={16} className="text-kova-cyan" />
            </button>
            <button
              data-testid="quick-nl-query"
              onClick={() => navigate('/nl')}
              className="w-full flex items-center justify-between p-3 border border-kova-cyan/30 bg-kova-cyan/5 hover:bg-kova-cyan/10 transition-colors text-left"
            >
              <span className="text-sm text-kova-cyan">Ask AI Assistant</span>
              <ArrowUpRight size={16} className="text-kova-cyan" />
            </button>
          </div>
        </motion.div>
      </div>

      {/* Recent Logs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card-brutal"
      >
        <div className="flex items-center justify-between p-4 border-b border-kova-cyan/12">
          <h2 className="font-heading font-bold text-lg">Recent Executions</h2>
          <button
            data-testid="view-all-logs"
            onClick={() => navigate('/logs')}
            className="text-kova-cyan text-sm font-mono flex items-center gap-1 hover:underline"
          >
            View all
            <CaretRight size={14} />
          </button>
        </div>
        
        {recentLogs.length > 0 ? (
          <div className="terminal-bg">
            <table className="w-full">
              <thead>
                <tr className="text-kova-slate text-xs font-mono uppercase tracking-wide">
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Method</th>
                  <th className="text-left p-3">URL</th>
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Duration</th>
                </tr>
              </thead>
              <tbody className="terminal-text">
                {recentLogs.map((log) => (
                  <tr 
                    key={log.log_id} 
                    className="border-t border-[#1a1a1a] hover:bg-kova-bg cursor-pointer"
                    onClick={() => navigate(`/logs?id=${log.log_id}`)}
                  >
                    <td className="p-3">
                      <span className={`flex items-center gap-2 ${getStatusColor(log.response_status)}`}>
                        {log.response_status < 400 ? (
                          <CheckCircle size={14} weight="fill" />
                        ) : (
                          <XCircle size={14} weight="fill" />
                        )}
                        {log.response_status}
                      </span>
                    </td>
                    <td className={`p-3 method-${log.request_method.toLowerCase()}`}>
                      {log.request_method}
                    </td>
                    <td className="p-3 text-kova-silv max-w-xs truncate">
                      {log.request_url}
                    </td>
                    <td className="p-3 text-kova-slate">
                      {formatTime(log.created_at)}
                    </td>
                    <td className="p-3 text-kova-slate">
                      {log.response_time_ms}ms
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-kova-slate font-mono text-sm">No executions yet</p>
            <button
              onClick={() => navigate('/connections')}
              className="mt-4 text-kova-cyan text-sm font-mono hover:underline"
            >
              Add your first API connection →
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
